'use client';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { use, useEffect, useState } from 'react';
import { trpc } from '@/app/_trpc/client';
import {
  AccountType,
  HelpPreference,
} from '@/autogenerated/@prisma/prisma-mongo-client';
import { DialogContext } from '@/components/features/dialogs/infra/context/DialogContext';
import { Button, ButtonProps } from '@/components/ui/button';

export function ToHelpButton({
  userName,
  profilePhoto,
  userId,
  accountType,
  size = 'medium',
  variant = 'secondary',
  className,
  onClick,
  matchId,
  idVerificationStatus,
  userHelpPreferences,
}: {
  userName: string;
  profilePhoto?: string;
  userId: string;
  accountType: AccountType;
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
  onClick?: () => void;
  matchId: string | null;
  idVerificationStatus: boolean;
  userHelpPreferences: HelpPreference[];
}) {
  const t = useTranslations('ToHelpButton');
  const { data: session, status } = useSession();
  const isLoggedIn = !!session;
  const { openDialog } = use(DialogContext);
  const router = useRouter();

  const { data: loggedInUser } = trpc.users.getById.useQuery(
    { id: session?.user?.id || '' },
    { enabled: !!session?.user?.id }
  );

  const [currentMatchId, setCurrentMatchId] = useState<string | null>(matchId);

  const { data: matches, isLoading: isLoadingMatchId } =
    trpc.matches.getMatchIds.useQuery(undefined, {
      enabled: isLoggedIn,
    });

  useEffect(() => {
    if (matches) {
      const match = matches.find((match) => match.userId === userId);
      if (match) {
        setCurrentMatchId(match.matchId);
      }
    }
  }, [matches, userId]);

  const isSameAccountType = session?.user?.accountType === accountType;
  if (isSameAccountType) {
    return null;
  }

  const handleHelpClick = async () => {
    if (onClick) {
      onClick();
    }

    if (status === 'loading' || isLoadingMatchId) {
      return null;
    }
    if (isLoggedIn) {
      const commonHelpPreferences = loggedInUser?.helpPreferences?.filter(
        (helpPreference) => userHelpPreferences.includes(helpPreference)
      );

      openDialog('init-match', {
        userName: userName,
        userImage: profilePhoto,
        userId: userId,
        showWarningMessage:
          accountType === AccountType.Refugee && !idVerificationStatus,
        commonHelpPreferences: commonHelpPreferences,
      });
    } else {
      openDialog('signup', {
        userName: userName.split(' ')[0],
      });
    }
  };

  const goToInbox = () => {
    if (currentMatchId) {
      router.push(`/inbox/${currentMatchId}`);
    }
    if (onClick) {
      onClick();
    }
  };

  const isMatched = isLoggedIn && currentMatchId;

  return (
    <Button
      className={className}
      variant={variant}
      onClick={isMatched ? goToInbox : handleHelpClick}
      size={size}
      loadingText={''}
    >
      {isMatched ? t('chat_with_me') : t('title', { accountType })}
    </Button>
  );
}
